/**
 * get the collection associated with this view, it is the grid collection
 * keep the map in-sync with the collection, ignoring features with no geometry
 * A nice feature might be to setup a mapping from the model similar to datasets
 * so we can query the GIS for geometry instead of asking our services to do it.
 *
 * For example, give this configuration:
 * moniker = Hansen.Property.Address
 * datafield = ADDRKEY
 * gis = //ags-server/featureserver/1
 *
 * Run a query against the GIS service to get the geometry of the address(es)
 * without involving the server at all
 */

import ol = require("ol");
import Portal = require("portal-gis");
import Utils = require("../utils");
import Base = require("./base-command");
import geo = require("../utils/geo");
import Converter = require("../utils/ol3/converter");
import Templates = require("../models/defaults");
import globals = require("../../globals");
import Backbone = require("backbone");
import _ = require("underscore");
const log = Utils.log;

const constants = {
    MarkerIndex: "_MARKER_INDEX_",
    ModelClicked: "model:clicked"
}

class Command extends Base<any> {

    static about = `
Listens for mediator events and posts them to the liferay listener.

Usage Example: 

\`\`\`xml
<COMMAND about="Listens for mediator events, re-interprets the arguments and triggers a new event on the civics channel" id="liferay-interpreter" mid="app/commands/liferay-interpreter" type="startup">
  <OPTIONS>
    <Values id="channel-name" value="civics-channel" />
    <Values id="liferay-echo-prefix" value="liferay" />
    <Values id="debug" value="false" />
    <Values about="take a snapshot of the feature(s)" id="snapshot" value="true" />
    <Values about="events to forward to liferay" id="forward" value="map:clicked,map:paging,civics:show-details,civics:zone-validation,civics:paging,show-info,show-feature-info,civics:map-click-results,flash-buffer,show-info-handler-controller" />
    <Values about="Creates a stub listener (liferay will not get events)" id="stub" value="false" />
    <Values about="write to console" id="trace" value="false" />
  </OPTIONS>
</COMMAND>
\`\`\`


`;

    static options = {
        "channel-name": {
            "about": "The radio channel to use when communication with liferay",
        },
        "forward": {
            "about": "Limit the events which should be forwarded to liferay.",
        },
        "liferay-echo-prefix": {
            "about": "Listens for mediator events, re-interprets the arguments and triggers a new event on the civics channel prefixing the name of the original event.",
        },
        "locatingproperties": {
            "about": "One or more attribute paths to probe for location information",
            "default": "address,property,parcel,instrument",
        },
        "snapshot": {
            "about": "take a snapshot of the feature(s)",
        },
        "stub": {
            "about": "Creates a fake liferay radio channel",
        },
    }

    private handles = <Array<{ off: () => void }>>[];

    destroy() {
        // perform necessary cleanup (if any)
        this.handles.forEach(h => h.off());
        this.handles = [];
    }

    //search the args hierarchy for a value
    findOption(name: string, args: any = this) {
        let arg = super.findArg(name, args);
        return arg && arg[name];
    }

    execute(args: any = this.args) {

        let mediator = this.mediator;
        let app = mediator.api;
        let channelName = this.options["channel-name"] || "civics-channel";
        let stub = this.options["stub"] === "true";
        let trace = this.findOption("trace") === "true";

        // for development purposes create a channel stub
        if (stub) {
            let radio = _.extend({}, Backbone.Events);
            mediator.model.set(channelName, radio);
        }

        Utils.once<Backbone.Events>(mediator.model, channelName, radio => {

            if (!radio) return;

            let echoPrefix = this.findOption("liferay-echo-prefix");
            if (echoPrefix) {
                let h = Utils.on(radio, "all", (name: string, options: any) => {
                    let names = Utils.split(name);
                    names.forEach(name => {
                        if (trace) console.log(`${echoPrefix}:${name}`, options);
                        this.mediator.events.trigger(`${echoPrefix}:${name}`, options);
                    });
                });
                this.handles.push(h);
            }

            /**
             * hard-coded listener for civics:show-details cleans up args and forwards as map:more-info
             * used as a way of identifying a single feature for special action
             */
            let doit = (eventName: string, args) => {
                let configuration = this.findOption("configuration", args);

                let layers = this.findOption("layers", args);
                let activeFeature = this.findOption("activeFeature", args);

                // hack to get the 'layers' associated with the active feature and not the 'layers' associated with later queries (ie zone count)
                if (activeFeature) {
                    let layerContainer = this.findArg("activeFeature", args);
                    layers = layerContainer.layers || layers;
                }

                let options = _.extend(configuration ? Utils.asDictionary(configuration.Options.Values) : {}, args.out);

                // provide rhythm with enough information to 'show more' details
                let result = {
                    // provide any command/event/layer/feature configured options
                    options: options,
                    // provide the attribute data of all features in layers
                    features: layers && _.flatten(layers.filter(l => !!l.features?.length).map(l => l.features)),
                    // provide the currently active features (when paging, the one being displayed)
                    selected: activeFeature ? [activeFeature] : [],
                    raw: {
                        eventName: eventName,
                        args: args
                    }
                };

                radio.trigger(eventName, result);

            };

            (Utils.split(this.findOption("forward") || ""))
                .forEach(eventName => mediator.on(eventName, args => doit(eventName, args)));

            let civicsLayer = app.configuration.Map.Layers.Layers.filter(l => l.id === "civics")[0];
            if (civicsLayer) {

                /**
                 * the filter has changed, expect the collection to be updated momentarily
                */
                let h = Utils.on(radio, "collection:cleared", () => {

                    app.mediator.mapView(view => {
                        // clear the civics layer when the civics collection is cleared
                        app.mediator.trigger("flash-buffer", {});
                        app.mediator.trigger("clear-features-from-layer", {});

                        // synthetic event to hide the popup window
                        view.controller.trigger("show-info", {
                            caller: constants.ModelClicked,
                            layers: [{ features: [] }]
                        });
                    });

                });
                this.handles.push(h);

                /**
                 * User clicked a row in civics, show info for that feature
                 */
                h = Utils.on(radio, "model:clicked", (model: Backbone.Model) => {
                    let m = model.toJSON ? model.toJSON() : model;
                    geo.beforeProcess({ data: m }, this.options["locatingproperties"]);
                    app.mediator.mapView(view => {
                        let coord = [m.xCoordinate, m.yCoordinate];
                        let canView = coord.every(v => !!v);

                        if (canView) {
                            view.panTo(coord);
                        } else {
                            console.warn("cannot find coordinates");
                        }

                        app.mediator.mapViewMap(map => {
                            if (canView) {
                                let converter = new Converter(map.getView().getProjection().getCode());

                                // find the ol3 source
                                let layers = map.getLayers().getArray()
                                    .filter(l => l instanceof ol.layer.Vector)
                                    .map(l => <ol.layer.Vector>l);

                                let olLayer = layers.filter(l => l.get("id") === civicsLayer.id)[0];
                                let s = olLayer.getSource();

                                let keys = [constants.MarkerIndex];

                                // get features associated with this model
                                let features = s.getFeatures()
                                    .filter(f => !!f.get("attributes"))
                                    .filter(f => keys.every(k => model.get(k) === f.get("attributes")[k]));

                                // include any within a clusted feature
                                s.getFeatures()
                                    .filter(f => !!f.get("features"))
                                    .forEach(f => {
                                        (<Array<ol.Feature>>f.get("features"))
                                            .filter(f => !!f.get("attributes"))
                                            .filter(f => keys.every(k => model.get(k) === f.get("attributes")[k]))
                                            .forEach(f => features.push(f));

                                    });

                                // links API layer with the underlying configuration (mostly for its commands)
                                let fakeLayer = converter.fromNativeFeatures(features);
                                Utils.extend(fakeLayer, { config: civicsLayer });

                                // synthetic event to show the popup for this feature
                                view.controller.trigger("show-info", {
                                    caller: constants.ModelClicked,
                                    layers: [fakeLayer]
                                });
                            } else {
                                // synthetic event to hide the popup window
                                view.controller.trigger("show-info", {
                                    caller: constants.ModelClicked,
                                    layers: [{ features: [] }]
                                });
                            }
                        });
                    });
                });
                this.handles.push(h);

                let renderCollection = (args: { collection: Backbone.Collection<any> }) => {
                    // clone the attributes and communicate map activities via radio
                    args.collection.models && args.collection.models.forEach((m, i) => m.set(constants.MarkerIndex, i));
                    let models = args.collection.toJSON();

                    /**
                     * ips-geoquery-processor does something very similar although it contains both the query and response processing
                     * so we'll use those ideas (and geo) to do the same thing here, which is to set the x/y attribute data based
                     * on locating properties and "Spatial" values
                     */
                    models.forEach(m => geo.beforeProcess({ data: m }, this.options["locatingproperties"]));

                    /* render only gis features within this collection */
                    log(`${models.length} items ready to map`);
                    models = models.filter(m => !!m.xCoordinate || !!m.yCoordinate);
                    log(`${models.length} items have a spatial data`);

                    civicsLayer.Features.Features = models.map(m => Templates.asFeatureTemplate({
                        id: m.id,
                        geom: `[${m.xCoordinate}, ${m.yCoordinate}]`,
                        Options: {
                            Values: [{
                                id: "symbology",
                                value: "should-not-match-any-symbology"
                            }]
                        },
                        Attributes: {
                            Values: Object.keys(m).map(k => ({ id: k, value: m[k] }))
                        }
                    }));

                    app.mergeLayers({
                        Layers: [civicsLayer]
                    });
                    // spoofs a controller command to zoom and pan to these markers

                    // zooms into a specific pin in a set of markers
                    let zoomLoc = models.find(m => m._ZOOM_TO_PIN_ === true);

                    if(zoomLoc){
                        //remove the attribute in the collection so we dont zoom to all        
                        //args.collection.models.forEach((m) => m.unset('_ZOOM_TO_PIN_'));
    
                         app.mediator.trigger("auto-zoom", {
                            out: {
                                layers: [{
                                    features: models.map(m => ({
                                        geometryType: "point",
                                        geometry: [zoomLoc.xCoordinate, zoomLoc.yCoordinate].map(parseFloat)
                                    }))
                                }]
                            }
                        });

                    }else{
                        app.mediator.trigger("auto-zoom", {
                            out: {
                                layers: [{
                                    features: models.map(m => ({
                                        geometryType: "point",
                                        geometry: [m.xCoordinate, m.yCoordinate].map(parseFloat)
                                    }))
                                }]
                            }
                        });
                    }


                };

                h = Utils.on(radio, "collection:updated", args => {
                    renderCollection(args);
                });
                this.handles.push(h);

                setTimeout(() => radio.trigger("map:ready", app), 0);

            }
        });

        this.trigger();
    }

}

export = Command;
